import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { MarineInsuranceManagementModel } from '@data-layer/models/marine-insurance';
import { enumPaymentState } from '@data-layer/enums/payments';
import { enumMarineInsurancePendingPaymentAction } from '@data-layer/enums/marine-insurance';
import { Router } from '@angular/router';

@Component({
  selector: 'app-marine-insurance-payment-management-card',
  templateUrl: './marine-insurance-payments-management-card.component.html',
  styleUrls: ['./marine-insurance-payments-management-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarineInsurancePaymentsManagementCardComponent {
  @Input() public marineInsurance: MarineInsuranceManagementModel;
  @Output() public confirmRefund: EventEmitter<void> = new EventEmitter<void>();

  public DEFAULT_CURRENCY_CODE: typeof DEFAULT_CURRENCY_CODE = DEFAULT_CURRENCY_CODE;

  constructor(private router: Router) {}

  public get paymentStatusColor(): string {
    let color: string = '#1845BC';

    if (
      [enumPaymentState.SUCCESS, enumPaymentState.DELAYED].includes(this.marineInsurance.paymentState) &&
      !this.marineInsurance.pendingPaymentAction
    ) {
      color = '#27AE60';
    }

    if (
      [enumPaymentState.SUCCESS, enumPaymentState.DELAYED].includes(this.marineInsurance.paymentState) &&
      this.marineInsurance.pendingPaymentAction === enumMarineInsurancePendingPaymentAction.ADDITIONAL_PAYMENT
    ) {
      color = '#FF9500';
    }

    if (
      [enumPaymentState.SUCCESS, enumPaymentState.DELAYED].includes(this.marineInsurance.paymentState) &&
      this.marineInsurance.pendingPaymentAction === enumMarineInsurancePendingPaymentAction.REFUND_PAYMENT
    ) {
      color = '#FF5C5C';
    }

    return color;
  }

  public get currentPaymentStatusText(): string {
    return `PAYMENT_STATE.${enumPaymentState[this.marineInsurance.paymentState]}`;
  }

  public get paymentStatusText(): string {
    if (
      [enumPaymentState.SUCCESS, enumPaymentState.DELAYED].includes(this.marineInsurance.paymentState) &&
      this.marineInsurance.pendingPaymentAction === enumMarineInsurancePendingPaymentAction.ADDITIONAL_PAYMENT
    ) {
      return 'PAYMENT_STATE.NEW_PAYMENT_REQUEST_SENT';
    }

    if (
      [enumPaymentState.SUCCESS, enumPaymentState.DELAYED].includes(this.marineInsurance.paymentState) &&
      this.marineInsurance.pendingPaymentAction === enumMarineInsurancePendingPaymentAction.REFUND_PAYMENT
    ) {
      return 'PAYMENT_STATE.PENDING_PARTIAL_REFUND';
    }

    return `PAYMENT_STATE.${enumPaymentState[this.marineInsurance.paymentState]}`;
  }

  public get isShowInitialValue(): boolean {
    return (
      this.isShowSecondLine ||
      (!this.marineInsurance?.updatedInsuredValue?.insuredAmount &&
        !this.marineInsurance?.certificate?.updatedProviderCost &&
        !this.marineInsurance?.certificate?.updatedHandlingFee)
    );
  }

  public get paymentStatusColorSuccess(): string {
    return '#27AE60';
  }

  public get paymentStatusColorRefunded(): string {
    return '#1845BC';
  }

  public get isShowSecondLine(): boolean {
    return (
      (this.marineInsurance?.pendingPaymentAction === enumMarineInsurancePendingPaymentAction.ADDITIONAL_PAYMENT &&
        !this.marineInsurance.updatedPaymentState) ||
      this.isRefundPending
    );
  }

  public get isRefundPending(): boolean {
    return (
      this.marineInsurance.pendingPaymentAction === enumMarineInsurancePendingPaymentAction.REFUND_PAYMENT &&
      !this.marineInsurance.partiallyRefundConfirmed
    );
  }

  public get isRefundNonPending(): boolean {
    return (
      this.marineInsurance.pendingPaymentAction === enumMarineInsurancePendingPaymentAction.REFUND_PAYMENT &&
      this.marineInsurance.partiallyRefundConfirmed
    );
  }

  public get isRefundNonPendingOrCompleted(): boolean {
    return this.isRefundNonPending || this.marineInsurance.paymentState === enumPaymentState.REFUNDED;
  }

  public get isRefundCompleted(): boolean {
    return this.marineInsurance.paymentState === enumPaymentState.REFUNDED;
  }

  public goToBill(): void {
    this.router.navigate(['bills-management'], { queryParams: { referenceNumber: this.marineInsurance.referenceNumber } });
  }
}
